import React from 'react'
import { Box, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material'
import type { AlertEvery, AlertSchedule } from '../../types/gotypes'
import { ALERT_EVERY_OPTIONS, DEFAULT_ALERT_TIME_ZONE, WEEKDAYS, isValidAt, scheduleForEvery } from '../../utils/alertSchedule'

export interface AlertSchedulePickerProps {
  value: AlertSchedule
  onChange: (next: AlertSchedule) => void
  disabled?: boolean
}

const DAYS = Array.from({ length: 31 }, (_, i) => i + 1)

/**
 * When a table or question alert runs: Daily, Weekly on a weekday or Monthly
 * on a day, at one time, in the zone the schedule carries (the viewer's own
 * for a new alert). A day above the month's length runs on its last day.
 */
const AlertSchedulePicker: React.FC<AlertSchedulePickerProps> = ({ value, onChange, disabled }) => {
  const atInvalid = !isValidAt(value.at)
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <FormControl size="small" sx={{ minWidth: 120 }} disabled={disabled}>
          <InputLabel id="alert-schedule-every-label">Runs</InputLabel>
          <Select
            labelId="alert-schedule-every-label"
            label="Runs"
            value={value.every}
            onChange={(e) => onChange(scheduleForEvery(value, e.target.value as AlertEvery))}
            inputProps={{ 'aria-label': 'Runs' }}
          >
            {ALERT_EVERY_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {value.every === 'weekly' && (
          <FormControl size="small" sx={{ minWidth: 140 }} disabled={disabled}>
            <InputLabel id="alert-schedule-weekday-label">Weekday</InputLabel>
            <Select
              labelId="alert-schedule-weekday-label"
              label="Weekday"
              value={value.weekday ?? 1}
              onChange={(e) => onChange({ ...value, weekday: Number(e.target.value) })}
              inputProps={{ 'aria-label': 'Weekday' }}
            >
              {WEEKDAYS.map((d, i) => (
                <MenuItem key={d} value={i}>{d}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {value.every === 'monthly' && (
          <FormControl size="small" sx={{ minWidth: 110 }} disabled={disabled}>
            <InputLabel id="alert-schedule-day-label">Day</InputLabel>
            <Select
              labelId="alert-schedule-day-label"
              label="Day"
              value={value.day ?? 1}
              onChange={(e) => onChange({ ...value, day: Number(e.target.value) })}
              inputProps={{ 'aria-label': 'Day of the month' }}
            >
              {DAYS.map((d) => (
                <MenuItem key={d} value={d}>{d}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <TextField
          label="At"
          type="time"
          size="small"
          value={value.at}
          onChange={(e) => onChange({ ...value, at: e.target.value })}
          disabled={disabled}
          error={atInvalid}
          helperText={atInvalid ? 'A time, such as 09:00' : undefined}
          sx={{ width: 130 }}
          slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'aria-label': 'At', step: 60 } }}
        />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        Times are in {value.timezone || DEFAULT_ALERT_TIME_ZONE}.
        {value.every === 'monthly' && (value.day ?? 1) > 28 ? " In a shorter month it runs on the month's last day." : ''}
      </Typography>
    </Box>
  )
}

export default AlertSchedulePicker
